import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import type { Animatable } from "@babylonjs/core/Animations/animatable";
import type { Animation } from "@babylonjs/core/Animations/animation";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";

/** Source-authored crane art follows existing puzzle parents and animations.
 * Optional load failures leave that parent's procedural visual intact.
 */
export class CraneVisuals {
  readonly ready: Promise<void>;
  private disposed = false;
  private logRetiring = false;
  private logModel: InstantiatedModel | null = null;
  private fades: Animatable[] = [];
  private armModel: InstantiatedModel | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly log: Mesh,
    private readonly pivot: TransformNode,
    private readonly armFallbacks: Mesh[],
    private readonly shadows: Pick<ShadowGenerator, "addShadowCaster" | "removeShadowCaster">,
  ) {
    this.ready = Promise.all([this.loadLog(), this.loadArm()]).then(() => {});
  }

  private async loadLog(): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/scrapyard_crane_log.glb");
      if (this.disposed || this.logRetiring || this.scene.isDisposed || this.log.isDisposed()) {
        model.dispose(); return;
      }
      for (const root of model.rootNodes) root.parent = this.log;
      for (const mesh of model.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = true;
        this.shadows.addShadowCaster(mesh);
      }
      this.logModel = model;
      this.log.isVisible = false;
    } catch (error) {
      if (!this.disposed) console.warn("[CraneVisuals] Keeping procedural log:", error);
    }
  }

  private async loadArm(): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/scrapyard_crane_arm.glb");
      if (this.disposed || this.scene.isDisposed || this.pivot.isDisposed()) {
        model.dispose(); return;
      }
      for (const root of model.rootNodes) root.parent = this.pivot;
      for (const mesh of model.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = true;
      }
      this.armModel = model;
      for (const mesh of this.armFallbacks) mesh.setEnabled(false);
    } catch (error) {
      if (!this.disposed) console.warn("[CraneVisuals] Keeping procedural arm:", error);
    }
  }

  prepareLogFade(fade: Animation, frames: number): void {
    // A late model must never replace the already-moving fallback. Babylon
    // beginAnimation on the collider does not animate descendant meshes;
    // start visible children explicitly in the same state-change callback.
    this.logRetiring = true;
    for (const mesh of this.logModel?.meshes ?? []) {
      this.fades.push(this.scene.beginDirectAnimation(mesh, [fade], 0, frames, false));
    }
  }

  finishLog(): void {
    this.logRetiring = true;
    for (const animation of this.fades) animation.stop();
    this.fades = [];
    for (const mesh of this.logModel?.meshes ?? []) this.shadows.removeShadowCaster(mesh);
    this.logModel?.dispose(); this.logModel = null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.finishLog();
    this.armModel?.dispose(); this.armModel = null;
  }
}
